import { _ } from "../../polyglot/polyglot.js";
import { required } from "../forms/validators.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FormBuilder from "../forms/FormBuilder.js";

/**
 * `KeyFigurePresetFormWidget` renders a form to edit the name and
 * description of a key figure preset.
 *
 * @param {keyFigurePreset} [spec.keyFigurePreset] - The preset to
 * edit. Can also be set through `setKeyFigurePreset()`.
 * @param {function} spec.updateCallback - Function called when the
 * user validates the form.
 * @param {function} spec.deleteCallback - Function called when the
 * user presses the delete button.
 */
export default class KeyFigurePresetFormWidget extends Widget {
	constructor({ keyFigurePreset, updateCallback, deleteCallback } = {}) {
		super(...arguments);
		this._keyFigurePreset = keyFigurePreset;
		this._updateCallback = updateCallback;
		this._deleteCallback = deleteCallback;

		this._buildForm();
	}

	setKeyFigurePreset(keyFigurePreset) {
		this._keyFigurePreset = keyFigurePreset;
		this._buildForm();
	}

	renderContentOn(html) {
		if (!this._keyFigurePreset) {
			return;
		}

		html.render(this._form);
	}

	_buildForm() {
		if (!this._keyFigurePreset) {
			return;
		}

		this._form = new FormBuilder({
			model: this._keyFigurePreset,
			dataTestName: "save",
			saveAction: this._updateCallback,
			saveLabel: _("Save info"),
			labelClass: "col-sm-4",
			inputClass: "col-sm-8",
			extraActions: this._extraActions.bind(this),
			showCancel: false,
		});

		this._form.input({
			label: _("Name"),
			name: "name",
			accessor: this._keyFigurePreset.getName.bind(this._keyFigurePreset),
			mutator: this._keyFigurePreset.setName.bind(this._keyFigurePreset),
			placeHolder: _("Profit Key Figures"),
			validator: required(),
			disabled: this._keyFigurePreset.isSystemPreset(),
		});

		this._form.input({
			label: _("Id"),
			name: "id",
			accessor: this._keyFigurePreset.getId.bind(this._keyFigurePreset),
			disabled: true,
		});

		this._form.input({
			label: _("Description"),
			name: "description",
			accessor: this._keyFigurePreset.getDescription.bind(
				this._keyFigurePreset,
			),
			mutator: this._keyFigurePreset.setDescription.bind(
				this._keyFigurePreset,
			),
			placeHolder: _("All Key Figures related to profits"),
			disabled: this._keyFigurePreset.isSystemPreset(),
		});

		if (this._keyFigurePreset.isSystemPreset()) {
			this._form.checkbox({
				label: _("Visible"),
				name: "isVisible",
				accessor: this._keyFigurePreset.isVisible.bind(
					this._keyFigurePreset,
				),
				mutator: this._keyFigurePreset.setVisibility.bind(
					this._keyFigurePreset,
				),
			});
		}
	}

	_delete() {
		this._confirmDeletion({
			callback: this._deleteCallback,
			okLabel: this._deleteLabel(),
			title: this._deleteLabel(),
			body: this._deleteConfirmationText(),
		});
	}

	_deleteConfirmationText() {
		if (this._keyFigurePreset.isSystemPreset()) {
			return _(
				"You are about to permanently reset {name}. This action cannot be undone. Are you sure?",
				{
					name: this._keyFigurePreset.getName(),
				},
			);
		}
		return _(
			"You are about to permanently delete {name}. This action cannot be undone. Are you sure?",
			{
				name: this._keyFigurePreset.getName(),
			},
		);
	}

	_extraActions() {
		return [this._renderDeleteButtonOn.bind(this)];
	}

	_renderDeleteButtonOn(html) {
		html.a(
			{
				klass: "btn cancel btn-danger margin-right",
				"data-test": "delete",
				click: () => this._delete(),
			},
			this._deleteLabel(),
		);
	}

	_deleteLabel() {
		return this._keyFigurePreset.isSystemPreset()
			? _("Reset Preset")
			: _("Delete Preset");
	}
}
